import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { loadPrinterCfg, printOrder } from "@/lib/printer";
import { loadSoundOn, saveSoundOn, startAlarm, stopAlarm } from "@/lib/alarm";

const SEEN_KEY = "primo_seen_orders";
const ACK_KEY = "primo_ack_orders";

const readSet = (key) => new Set(JSON.parse(localStorage.getItem(key) || "[]"));
const writeSet = (key, set) => localStorage.setItem(key, JSON.stringify([...set].slice(-500)));

export function useOrderWatcher() {
  const first = useRef(true);
  const pending = useRef([]);
  const [ringing, setRinging] = useState(false);
  const [soundOn, setSoundOn] = useState(loadSoundOn);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders", { params: { status: "pendente" } });
      pending.current = data;
      const seen = readSet(SEEN_KEY);
      const fresh = data.filter((o) => !seen.has(o.id));
      if (fresh.length && !first.current) {
        const cfg = loadPrinterCfg();
        const { data: store } = await api.get("/settings");
        for (const o of fresh.slice().reverse()) {
          toast.success(`Novo pedido #${o.number} - ${o.customer_name}`);
          if (cfg.autoPrint) await printOrder(o, store).catch((e) => toast.error(`Falha ao imprimir: ${e.message}`));
        }
      }
      fresh.forEach((o) => seen.add(o.id));
      writeSet(SEEN_KEY, seen);
      first.current = false;
      const ack = readSet(ACK_KEY);
      setRinging(data.some((o) => !ack.has(o.id)));
      window.dispatchEvent(new Event("orders:refresh"));
    } catch (e) {
      /* polling silently retries */
    }
  }, []);

  useEffect(() => {
    check();
    const t = setInterval(check, 15000);
    return () => { clearInterval(t); stopAlarm(); };
  }, [check]);

  useEffect(() => {
    if (ringing && soundOn) startAlarm();
    else stopAlarm();
  }, [ringing, soundOn]);

  const silence = useCallback(() => {
    const ack = readSet(ACK_KEY);
    pending.current.forEach((o) => ack.add(o.id));
    writeSet(ACK_KEY, ack);
    setRinging(false);
  }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((on) => { saveSoundOn(!on); return !on; });
  }, []);

  return { ringing, pendingCount: pending.current.length, silence, soundOn, toggleSound };
}
